/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { BooleanRadioButton, BooleanRadioButtonGroup, ExternalLink, Note } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { BuiltInFeedResource } from "@octopusdeploy/octopus-server-client";
import { repository } from "~/clientInstance";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import SaveDialogLayout from "~/components/DialogLayout/SaveDialogLayout";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { Text } from "~/components/form";
import styles from "./style.module.less";
interface EditBuiltInRepositoryRetentionProps {
    afterConfigurationUpdated(updated: BuiltInFeedResource): void;
}
interface EditBuiltInRepositoryRetentionState extends DataBaseComponentState {
    loaded: boolean;
    keepFor?: number;
    keepAllAssociatedPackages?: boolean;
    keepForever: boolean;
}
class EditBuiltInRepositoryRetention extends DataBaseComponent<EditBuiltInRepositoryRetentionProps, EditBuiltInRepositoryRetentionState> {
    constructor(props: EditBuiltInRepositoryRetentionProps) {
        super(props);
        this.state = {
            loaded: false,
            keepForever: false,
            keepAllAssociatedPackages: true,
        };
    }
    async componentDidMount() {
        return this.doBusyTask(() => this.load());
    }
    async load() {
        const config = await repository.Feeds.getBuiltIn();
        this.setState({
            loaded: true,
            keepFor: config.DeleteUnreleasedPackagesAfterDays,
            keepForever: !config.DeleteUnreleasedPackagesAfterDays,
            keepAllAssociatedPackages: !config.DeletePackagesAssociatedWithReleases,
        });
    }
    async save() {
        return this.doBusyTask(async () => {
            const config = await repository.Feeds.getBuiltIn();
            config.DeleteUnreleasedPackagesAfterDays = this.state.keepForever ? null! : this.state.keepFor;
            config.DeletePackagesAssociatedWithReleases = this.state.keepForever ? false : !this.state.keepAllAssociatedPackages;
            const updated = await repository.Feeds.modify(config);
            this.props.afterConfigurationUpdated(updated as BuiltInFeedResource);
        });
    }
    render() {
        const daysField = this.state.loaded && !this.state.keepForever && <Text label="days" type="number" min={1} className={styles.daysField} value={this.state.keepFor!.toString()} onChange={(v) => this.setState({ keepFor: parseFloat(v) })}/>;
        const associatedPackagesRadioGroup = this.state.loaded && !this.state.keepForever && (<BooleanRadioButtonGroup onChange={(v) => this.setState({ keepAllAssociatedPackages: v })} value={this.state.keepAllAssociatedPackages!} autoFocus>
                <BooleanRadioButton value={true} label="Keep all packages used by stored releases and runbooks runs" isDefault={true}/>
                <BooleanRadioButton value={false} label="Delete all packages after the number of days specified above"/>
            </BooleanRadioButtonGroup>);
        const body = this.state.loaded && (<div>
                <span className={styles.cardSubTitle}>How long after publishing should packages be kept?</span>
                <BooleanRadioButtonGroup onChange={(v) => (v ? this.setState({ keepForever: true, keepAllAssociatedPackages: true }) : this.setState({ keepForever: false, keepFor: 30, keepAllAssociatedPackages: true }))} value={this.state.keepForever} autoFocus>
                    <BooleanRadioButton value={true} label="Forever"/>
                    <BooleanRadioButton value={false} label="A limited time"/>
                </BooleanRadioButtonGroup>

                <div className={styles.limitedTimeOptions}>
                    {daysField}
                    {isFeatureToggleEnabled("DecoupleReleasesFromBuiltInPackageRetentionFeatureToggle") && associatedPackagesRadioGroup}
                </div>

                <Note style={{ marginTop: space[16] }}>
                    Packages are deleted automatically from the built-in repository after the period specified above with the exception of packages used on our dashboards. Please note that deleting packages will prevent the deployment of the
                    associated release. See our <ExternalLink href="RetentionPolicies">documentation</ExternalLink> for further information.
                </Note>
            </div>);
        return (<SaveDialogLayout title="Built-in Repository Retention" busy={this.state.busy} errors={this.errors} onSaveClick={() => this.save()}>
                {body}
            </SaveDialogLayout>);
    }
    static displayName = "EditBuiltInRepositoryRetention";
}
export default EditBuiltInRepositoryRetention;
